import { Grid, Typography } from '@mui/material';
import {
  TabPanel,
  TabLabel,
  TabHead,
  TabProvider,
  Tooltip,
  Icon,
  useDontMountAtFirst,
  getDataIdGenerator,
  LinkButton
} from '@hevoio/hevo-ui-toolkit';
import { Field, useFormikContext } from 'formik';
import React, { useState } from 'react';
import { HevoEntity } from '../../../../core/interfaces/hevo-entity';
import AdvancedConfig from '../../../../connector/connector-form/components/advanced-config-wrapper/AdvancedConfigWrapper';
import { HdFormikDropDown } from '../../../../form/formik/components';
import { GoogleAuth } from '../../../../connector/connector-form/components/google-auth/GoogleAuthFormik';
import {
  LoadedAt,
  SanitizeName
} from '../../../../connector/connector-form/components/connector-reusable-options/ConnectorReusableOptions';
import HdFormControl from '../../../../form/components/form-control/HdFormControl';
import HdSetupGuideLink from '../../../../docs/components/setup-guide-link/HdSetupGuideLink';
import useAnalyticsTracker from '../../../../analytics/hooks/useAnalyticsTracker';
import useTeamSettingsService from '../../../team/hooks/useTeamSettingsService';
import { ConfigDestinationTypeBaseProps } from '../interface';
import BigQueryEdit from './bigQueryEdit';
import { AUTH_FORM_FIELD_NAME, BigqueryResourceType } from './model';
import { StreamingWritesConfig } from './StreamingWritesConfig';
import styles from './BigQuery.module.scss';
import { ResourceTab } from './tabContainer';
import { BigqueryDestinationTrackingActions } from './tracking';
import {
  checkForUserPermission,
  fetchProjectList,
  fetchResourceListFn,
  getBigQueryAuthAccountInfo,
  getBucketName,
  getDatasetName
} from './utils';

export default function BigQuery(props: ConfigDestinationTypeBaseProps) {
  const { isEditing, hevoEntityFor } = props;

  if (isEditing || hevoEntityFor === HevoEntity.ACTIVATION || hevoEntityFor === HevoEntity.MODEL) {
    return <BigQueryEdit {...props} />;
  }

  return <BigQueryCreate {...props} />;
}

export const bucketErrorMessage = () => (
  <div>
    Bucket Name must:
    <ul className='pl-3'>
      <li>Contain only lowercase letters, numbers, dashes, underscores and dots</li>
      <li>Start and end with a number or letter</li>
      <li>Contain 3 - 63 characters</li>
    </ul>
  </div>
);

export const datasetErrorMessage = () => (
  <div>
    Dataset Name must:
    <ul className='pl-3'>
      <li>Contain up to 1024 characters</li>
      <li>Contain only letters(upper or lower case), numbers, and underscores</li>
    </ul>
  </div>
);

function RefreshProjectIDDropdownAdornment({ refreshProjectList }) {
  const { eventTrack } = useAnalyticsTracker();

  return (
    <LinkButton
      className={`${styles.refreshAdornment} text-primary `}
      tag='a'
      onMouseDown={() => {
        refreshProjectList();
        eventTrack({
          action: BigqueryDestinationTrackingActions.PROJECT_REFRESH_LIST_CLICK
        });
      }}
      direction='right'
      icon='refresh'
      dataId='bigquery-project-refresh'
    >
      Refresh
    </LinkButton>
  );
}

export function BigQueryCreate(props: ConfigDestinationTypeBaseProps) {
  const { formMetaData, hevoEntityFor, destinationTypeIdentifier } = props;

  const { getTeamSettings } = useTeamSettingsService();

  const { values, isSubmitting, setValues } = useFormikContext<{
    auth?: any;
    projectId: { id: string };
    authorisedAccount: string;
    streamingWrites: boolean;
  }>();

  const { projects = [], buckets = [], datasets = [], clusterRegion } = formMetaData;
  const isStreamModeAllowed = getTeamSettings()?.wh_streaming_writes === true;
  const useStreamMode = isStreamModeAllowed && values.streamingWrites;
  const [projectList, setProjectList] = useState(projects);

  const { eventTrack } = useAnalyticsTracker();

  const [showProjectLoading, setProjectLoading] = useState(false);
  const [showNoProjectError, setNoProjectError] = useState(!projectList.length && !!values.auth);

  const destinationTypeTrackingProps = {
    destinationType: destinationTypeIdentifier,
    context: hevoEntityFor
  };

  const refreshProjectList = () => {
    const { type, id } = getBigQueryAuthAccountInfo(values, AUTH_FORM_FIELD_NAME);
    setProjectLoading(true);
    setProjectList([]);
    setNoProjectError(false);

    fetchProjectList(type, id)
      .then(response => {
        setProjectLoading(false);
        setProjectList(response);

        if (!response.length) {
          eventTrack({
            action: BigqueryDestinationTrackingActions.PROJECT_LIST_EMPTY
          });
          setNoProjectError(true);
        }
      })
      .catch(_ => {
        setProjectLoading(false);
        setProjectList([]);
      });
  };

  const handleProjectChange = project => {
    setValues(prevValues => ({
      ...prevValues,
      projectId: project,
      bucket: null,
      dataset: null
    }));
  };

  useDontMountAtFirst(() => {
    handleProjectChange(null);
    setProjectList([]);
    refreshProjectList();
  }, [values.auth?.id]);

  const dataIdGenerator = getDataIdGenerator('bigquery-create');

  return (
    <>
      <Grid container spacing={2}>
        <Grid item sm={12} md={12}>
          <HdFormControl>
            <Field
              name={AUTH_FORM_FIELD_NAME}
              provider='BIGQUERY'
              displayName='Big Query'
              hevoEntity={hevoEntityFor}
              component={GoogleAuth}
            />
          </HdFormControl>
        </Grid>

        <Grid item md={12} sm={12}>
          <HdFormControl>
            <Field
              required
              label='Project ID'
              name='projectId'
              showLoading={showProjectLoading}
              TopAdornment={RefreshProjectIDDropdownAdornment}
              options={projectList}
              topAdornmentProps={{
                refreshProjectList
              }}
              onChangeEventHandler={handleProjectChange}
              noOptionsText='No Projects found in your account.'
              helperText={
                showNoProjectError ? (
                  <Typography variant='caption' className={styles.errorCaption}>
                    Your Bigquery account does not have a Google Project. Please create one and try
                    again.
                  </Typography>
                ) : (
                  <Typography variant='caption'>
                    Select the Project of your BigQuery instance.{' '}
                    <HdSetupGuideLink
                      setupGuideLink='/destinations/data-warehouses/google-bigquery/#configure-google-bigquery-as-a-destination'
                      section='destination-settings'
                      label='project-create-help-text'
                      properties={destinationTypeTrackingProps}
                      size='sm'
                      icon='right'
                      dataId={dataIdGenerator('project')}
                    >
                      Learn More
                    </HdSetupGuideLink>
                  </Typography>
                )
              }
              component={HdFormikDropDown}
            />
          </HdFormControl>
        </Grid>
      </Grid>

      <div className='border border-radius-md mb-7 w-100 overflow-hidden'>
        <TabProvider defaultTab='Dataset'>
          <TabHead isScrollable compact>
            <TabLabel
              compact
              key='Dataset'
              value='Dataset'
              label={
                <>
                  Dataset&nbsp;

                  <Tooltip title='Hevo will load data to this Dataset' dataId='dataset'>
                    <div>
                      <Icon className='ml-1' name='info' />
                    </div>
                  </Tooltip>
                </>
              }
            />

            {!useStreamMode && (
              <TabLabel
                compact
                key='GCS'
                value='GCS'
                label={
                  <>
                    GCS Bucket&nbsp;

                    <Tooltip title='Hevo uses a GCS bucket to load data to BigQuery' dataId='gcs'>
                      <div>
                        <Icon className='ml-1' name='info' />
                      </div>
                    </Tooltip>
                  </>
                }
              />
            )}
          </TabHead>

          <div className='flex-1'>
            <TabPanel
              key='Dataset'
              forceOpen={isSubmitting}
              value='Dataset'
              activeTabClassName='h-100'
              useLegacyKeepMounted
            >
              <ResourceTab
                label='Dataset'
                defaultResourceList={datasets}
                defaultLocation={clusterRegion}
                resource={BigqueryResourceType.DATASET}
                autoCreateResourceName={getDatasetName}
                checkIfUserHasCreatePermission={checkForUserPermission}
                fetchResourceList={fetchResourceListFn}
                dataId={dataIdGenerator(BigqueryResourceType.DATASET)}
              />
            </TabPanel>

            {!useStreamMode && (
              <TabPanel
                key='GCS'
                forceOpen={isSubmitting}
                value='GCS'
                activeTabClassName='h-100'
                useLegacyKeepMounted
              >
                <ResourceTab
                  label='GCS bucket'
                  defaultResourceList={buckets}
                  defaultLocation={clusterRegion}
                  resource={BigqueryResourceType.BUCKET}
                  autoCreateResourceName={getBucketName}
                  checkIfUserHasCreatePermission={checkForUserPermission}
                  fetchResourceList={fetchResourceListFn}
                  dataId={dataIdGenerator(BigqueryResourceType.BUCKET)}
                />
              </TabPanel>
            )}
          </div>
        </TabProvider>
      </div>

      <AdvancedConfig showBorderBottom>
        <div className='w-100'>
          <LoadedAt />

          <SanitizeName
            dataId={dataIdGenerator('sanitize-name')}
          />

          {isStreamModeAllowed ? (
            <StreamingWritesConfig dataId={dataIdGenerator('')} />
          ) : null}
        </div>
      </AdvancedConfig>
    </>
  );
}
